/**
 * PRD.261 — Patient Package Sales and Ownership.
 *
 * Imperative operations against the local PowerSync DB that create and maintain
 * patient-owned package records from invoice package lines. Pure status /
 * validity logic lives in `@helios/data-access` (patient-packages-logic) and is
 * unit-tested there; this module orchestrates the reads/writes.
 *
 * Scope boundary: ownership + snapshots only. No redemption, no stock
 * deduction, no redemption invoices (PRD.262).
 */

import {
  canTransition,
  derivePaymentStatus,
  nextRedemptionStatus,
  planPatientPackageCreation,
  type PackageChildInput,
  type PackageRedemptionStatus,
} from '@helios/data-access';

/** Minimal shape of the PowerSync DB handle used here. */
interface PowerSyncLike {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
  getAll: <T>(sql: string, params?: unknown[]) => Promise<T[]>;
}

export interface CreatePatientPackagesResult {
  created: number;
  /** Package lines that were skipped, with a user-facing reason. */
  skipped: { packageName: string; reason: string }[];
}

interface InvoiceRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  patient_id: string | null;
  date_iso: string | null;
  grand_total: number | null;
}

interface PackageLineRow {
  invoice_item_id: string;
  package_id: string;
  item_name: string | null;
  sku: string | null;
  selling_price: number | null;
  validity_months: number | null;
}

interface CompositionRow {
  child_type: string | null;
  child_inventory_item_id: string | null;
  child_appointment_service_id: string | null;
  quantity: number | null;
  included_unit_price: number | null;
  sort_order: number | null;
  child_item_name: string | null;
  child_sku: string | null;
  child_item_type: string | null;
  child_selling_price: number | null;
}

/**
 * Create patient package ownership from an invoice's package lines. Idempotent
 * by `source_invoice_item_id` — safe to call on every invoice save / offline
 * replay. Skips package lines whose definition has no active included items
 * (returns a reason instead of creating an empty, unredeemable package).
 */
export async function createPatientPackagesFromInvoice(params: {
  db: PowerSyncLike;
  invoiceId: string;
  userId?: string | null;
}): Promise<CreatePatientPackagesResult> {
  const { db, invoiceId } = params;
  const result: CreatePatientPackagesResult = { created: 0, skipped: [] };

  const invoiceRows = await db.getAll<InvoiceRow>(
    `SELECT id, org_id, clinic_id, patient_id, date_iso, grand_total FROM invoices WHERE id = ?`,
    [invoiceId],
  );
  const invoice = invoiceRows[0];
  if (!invoice) return result;
  const orgId = invoice.org_id;
  const patientId = invoice.patient_id;
  if (!orgId || !patientId) return result;

  const lines = await db.getAll<PackageLineRow>(
    `SELECT
       it.id            AS invoice_item_id,
       p.id             AS package_id,
       p.item_name      AS item_name,
       p.sku            AS sku,
       p.selling_price  AS selling_price,
       p.validity_months AS validity_months
     FROM invoice_items it
     JOIN inventory_items p ON p.id = it.inventory_item_id
     WHERE it.invoice_id = ?
       AND p.item_type = 'package'`,
    [invoiceId],
  );
  if (lines.length === 0) return result;

  const purchaseDate = (invoice.date_iso ?? new Date().toISOString()).slice(0, 10);

  // Existing ownership for this invoice → drives the planner's idempotency.
  const existingRows = await db.getAll<{ source_invoice_item_id: string }>(
    `SELECT source_invoice_item_id FROM patient_packages
      WHERE org_id = ? AND source_invoice_id = ? AND deleted_at IS NULL`,
    [orgId, invoiceId],
  );

  // Load active composition once per distinct package on the invoice.
  const compositionByPackage: Record<string, PackageChildInput[]> = {};
  for (const packageId of [...new Set(lines.map((l) => l.package_id))]) {
    // PRD.267 — a child is either an inventory item or an appointment service;
    // resolve display name/type/price from whichever join matches.
    const composition = await db.getAll<CompositionRow>(
      `SELECT
         pi.child_type                   AS child_type,
         pi.child_inventory_item_id      AS child_inventory_item_id,
         pi.child_appointment_service_id AS child_appointment_service_id,
         pi.quantity                     AS quantity,
         pi.included_unit_price          AS included_unit_price,
         pi.sort_order                   AS sort_order,
         COALESCE(c.item_name, s.name)   AS child_item_name,
         c.sku                           AS child_sku,
         CASE WHEN pi.child_appointment_service_id IS NOT NULL
              THEN 'service' ELSE c.item_type END AS child_item_type,
         COALESCE(c.selling_price, s.price_cents / 100.0) AS child_selling_price
       FROM inventory_package_items pi
       LEFT JOIN inventory_items c ON c.id = pi.child_inventory_item_id
       LEFT JOIN appointment_services s ON s.id = pi.child_appointment_service_id
       WHERE pi.package_inventory_item_id = ?
         AND pi.deleted_at IS NULL
       ORDER BY pi.sort_order ASC`,
      [packageId],
    );
    compositionByPackage[packageId] = composition.map((c) => ({
      childType:
        c.child_appointment_service_id != null || c.child_type === 'appointment_service'
          ? ('appointment_service' as const)
          : ('inventory_item' as const),
      childInventoryItemId: c.child_inventory_item_id,
      childAppointmentServiceId: c.child_appointment_service_id,
      quantity: c.quantity,
      includedUnitPrice: c.included_unit_price,
      sortOrder: c.sort_order,
      name: c.child_item_name,
      code: c.child_sku,
      itemType: c.child_item_type,
      sellingPrice: c.child_selling_price,
    }));
  }

  // Pure planner decides what to create (idempotency + snapshots + skips).
  const plan = planPatientPackageCreation({
    purchaseDate,
    lines: lines.map((l) => ({
      invoiceItemId: l.invoice_item_id,
      packageId: l.package_id,
      name: l.item_name,
      code: l.sku,
      price: l.selling_price,
      validityMonths: l.validity_months,
    })),
    existingSourceItemIds: existingRows.map((r) => r.source_invoice_item_id),
    compositionByPackage,
    newId: () => crypto.randomUUID(),
  });

  const now = new Date().toISOString();
  for (const p of plan.packages) {
    await db.execute(
      `INSERT INTO patient_packages (
         id, org_id, patient_id, clinic_id,
         source_invoice_id, source_invoice_item_id, package_inventory_item_id,
         package_name_snapshot, package_code_snapshot, package_price_snapshot,
         purchase_date, valid_until, validity_months_snapshot,
         payment_status, redemption_status,
         created_at, updated_at, deleted_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'unpaid', 'pending_payment', ?, ?, NULL)`,
      [
        p.id,
        orgId,
        patientId,
        invoice.clinic_id ?? null,
        invoiceId,
        p.source_invoice_item_id,
        p.package_inventory_item_id,
        p.package_name_snapshot,
        p.package_code_snapshot,
        p.package_price_snapshot,
        p.purchase_date,
        p.valid_until,
        p.validity_months_snapshot,
        now,
        now,
      ],
    );
  }
  for (const it of plan.items) {
    await db.execute(
      `INSERT INTO patient_package_items (
         id, org_id, patient_package_id,
         child_type, child_inventory_item_id, child_appointment_service_id,
         item_name_snapshot, item_code_snapshot, item_type_snapshot,
         total_quantity, redeemed_quantity, remaining_quantity,
         unit_price_snapshot, sort_order, status,
         created_at, updated_at, deleted_at
       ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 'available', ?, ?, NULL)`,
      [
        it.id,
        orgId,
        it.patient_package_id,
        it.child_type,
        it.child_inventory_item_id,
        it.child_appointment_service_id,
        it.item_name_snapshot,
        it.item_code_snapshot,
        it.item_type_snapshot,
        it.total_quantity,
        it.redeemed_quantity,
        it.remaining_quantity,
        it.unit_price_snapshot,
        it.sort_order,
        now,
        now,
      ],
    );
  }

  result.created = plan.packages.length;
  result.skipped = plan.skipped;

  // Reconcile statuses in case the invoice already has payments at save time.
  await syncPatientPackagePaymentStatusFromInvoice({ db, invoiceId });

  return result;
}

/**
 * Recompute patient package payment/redemption status from the source invoice's
 * settlement (PRD.261 §10). Idempotent and re-runnable — call after any payment
 * insert/edit/delete. Activates a still-pending package on first payment.
 */
export async function syncPatientPackagePaymentStatusFromInvoice(params: {
  db: PowerSyncLike;
  invoiceId: string;
}): Promise<void> {
  const { db, invoiceId } = params;

  const invRows = await db.getAll<{ grand_total: number | null }>(
    `SELECT grand_total FROM invoices WHERE id = ?`,
    [invoiceId],
  );
  const invoice = invRows[0];
  if (!invoice) return;

  const packages = await db.getAll<{
    id: string;
    payment_status: string | null;
    redemption_status: string | null;
    activated_at: string | null;
  }>(
    `SELECT id, payment_status, redemption_status, activated_at
       FROM patient_packages
      WHERE source_invoice_id = ? AND deleted_at IS NULL`,
    [invoiceId],
  );
  if (packages.length === 0) return;

  const paidRows = await db.getAll<{ paid_cents: number }>(
    `SELECT COALESCE(SUM(amount_cents), 0) AS paid_cents FROM invoice_payments WHERE invoice_id = ?`,
    [invoiceId],
  );
  const paidCents = paidRows[0]?.paid_cents ?? 0;
  const grandCents = Math.round((invoice.grand_total ?? 0) * 100);
  const paymentStatus = derivePaymentStatus(paidCents, grandCents);

  const now = new Date().toISOString();
  for (const pkg of packages) {
    if (pkg.redemption_status === 'voided') continue;
    const currentRedemption = (pkg.redemption_status ??
      'pending_payment') as PackageRedemptionStatus;
    const newRedemption = nextRedemptionStatus(currentRedemption, paymentStatus);
    const becomingActive = newRedemption === 'active' && !pkg.activated_at;
    const changed =
      pkg.payment_status !== paymentStatus ||
      pkg.redemption_status !== newRedemption ||
      becomingActive;
    if (!changed) continue;

    await db.execute(
      `UPDATE patient_packages
          SET payment_status = ?, redemption_status = ?,
              activated_at = COALESCE(activated_at, ?),
              activation_reason = COALESCE(activation_reason, ?),
              updated_at = ?
        WHERE id = ?`,
      [
        paymentStatus,
        newRedemption,
        becomingActive ? now : null,
        becomingActive ? 'first_payment' : null,
        now,
        pkg.id,
      ],
    );
  }
}

/**
 * Void patient packages tied to an invoice that is being deleted/cancelled
 * (PRD.261 §10, §11). Sets voided statuses + soft-deletes the ownership and its
 * item snapshots so they disappear from the patient view.
 */
export async function voidPatientPackagesForInvoice(params: {
  db: PowerSyncLike;
  invoiceId: string;
  userId?: string | null;
}): Promise<number> {
  const { db, invoiceId, userId = null } = params;
  const packages = await db.getAll<{ id: string }>(
    `SELECT id FROM patient_packages WHERE source_invoice_id = ? AND deleted_at IS NULL`,
    [invoiceId],
  );
  const now = new Date().toISOString();
  for (const pkg of packages) {
    await db.execute(
      `UPDATE patient_packages
          SET payment_status = 'voided', redemption_status = 'voided',
              voided_at = ?, voided_by = ?, void_reason = 'source_invoice_deleted',
              updated_at = ?, deleted_at = ?
        WHERE id = ?`,
      [now, userId, now, now, pkg.id],
    );
    await db.execute(
      `UPDATE patient_package_items
          SET deleted_at = ?, updated_at = ?
        WHERE patient_package_id = ? AND deleted_at IS NULL`,
      [now, now, pkg.id],
    );
  }
  return packages.length;
}

// ─── PRD.262 §12 — Manual status controls (admin/billing gated in the UI) ───

export interface StatusChangeResult {
  ok: boolean;
  error?: string;
}

async function loadPackageStatus(
  db: PowerSyncLike,
  patientPackageId: string,
): Promise<{ redemption_status: string | null; deleted_at: string | null } | null> {
  const rows = await db.getAll<{ redemption_status: string | null; deleted_at: string | null }>(
    `SELECT redemption_status, deleted_at FROM patient_packages WHERE id = ?`,
    [patientPackageId],
  );
  return rows[0] ?? null;
}

/** Manually activate a pending_payment package (requires a reason). */
export async function activatePatientPackage(params: {
  db: PowerSyncLike;
  patientPackageId: string;
  userId?: string | null;
  reason?: string | null;
}): Promise<StatusChangeResult> {
  const { db, patientPackageId, userId = null, reason = null } = params;
  const pkg = await loadPackageStatus(db, patientPackageId);
  if (!pkg) return { ok: false, error: 'Package not found.' };
  const status = (pkg.redemption_status ?? '') as PackageRedemptionStatus;
  if (!canTransition({ redemptionStatus: status, deletedAt: pkg.deleted_at })) {
    return { ok: false, error: 'This package cannot be activated.' };
  }
  if (status !== 'pending_payment') {
    return { ok: false, error: 'Only a pending-payment package can be activated.' };
  }
  const now = new Date().toISOString();
  await db.execute(
    `UPDATE patient_packages
        SET redemption_status = 'active',
            activated_at = COALESCE(activated_at, ?),
            activated_by = ?, activation_reason = ?, updated_at = ?
      WHERE id = ?`,
    [now, userId, reason?.trim() || 'manual activation', now, patientPackageId],
  );
  return { ok: true };
}

/** Suspend an active package to block future redemption (requires a reason). */
export async function suspendPatientPackage(params: {
  db: PowerSyncLike;
  patientPackageId: string;
  userId?: string | null;
  reason: string;
}): Promise<StatusChangeResult> {
  const { db, patientPackageId, userId = null, reason } = params;
  if (!reason.trim()) return { ok: false, error: 'A reason is required to suspend a package.' };
  const pkg = await loadPackageStatus(db, patientPackageId);
  if (!pkg) return { ok: false, error: 'Package not found.' };
  const status = (pkg.redemption_status ?? '') as PackageRedemptionStatus;
  if (!canTransition({ redemptionStatus: status, deletedAt: pkg.deleted_at })) {
    return { ok: false, error: 'This package cannot be suspended.' };
  }
  if (status !== 'active') {
    return { ok: false, error: 'Only an active package can be suspended.' };
  }
  const now = new Date().toISOString();
  await db.execute(
    `UPDATE patient_packages
        SET redemption_status = 'suspended',
            suspended_at = ?, suspended_by = ?, suspension_reason = ?, updated_at = ?
      WHERE id = ?`,
    [now, userId, reason.trim(), now, patientPackageId],
  );
  return { ok: true };
}

/** Reactivate a suspended package. */
export async function reactivatePatientPackage(params: {
  db: PowerSyncLike;
  patientPackageId: string;
  userId?: string | null;
}): Promise<StatusChangeResult> {
  const { db, patientPackageId, userId = null } = params;
  const pkg = await loadPackageStatus(db, patientPackageId);
  if (!pkg) return { ok: false, error: 'Package not found.' };
  const status = (pkg.redemption_status ?? '') as PackageRedemptionStatus;
  if (!canTransition({ redemptionStatus: status, deletedAt: pkg.deleted_at })) {
    return { ok: false, error: 'This package cannot be reactivated.' };
  }
  if (status !== 'suspended') {
    return { ok: false, error: 'Only a suspended package can be reactivated.' };
  }
  const now = new Date().toISOString();
  await db.execute(
    `UPDATE patient_packages
        SET redemption_status = 'active', suspended_at = NULL, updated_at = ?,
            activated_by = ?
      WHERE id = ?`,
    [now, userId, patientPackageId],
  );
  return { ok: true };
}
